import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, Package, Calendar, CreditCard, ArrowRight } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuthContext } from '../../contexts/AuthContext';

export default function OmisePaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuthContext();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<any>(null);
  const [error, setError] = useState('');

  const chargeId = searchParams.get('charge_id');
  const type = searchParams.get('type'); // 'subscription', 'addon', or 'free_activation'

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // For free activation, no charge_id needed
    if (type === 'free_activation') {
      loadFreeActivationData();
      return;
    }

    if (!chargeId) {
      setError('ไม่พบข้อมูลการชำระเงิน');
      setLoading(false);
      return;
    }

    loadPaymentData();
  }, [currentUser, chargeId, type]);

  const loadFreeActivationData = async () => {
    try {
      setLoading(true);
      
      // Get user's subscription data
      const userRef = doc(db, 'users', currentUser!.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setSubscription({
          current_plan: userData.subscriptionTier,
          billing_cycle: userData.subscriptionTier?.includes('yearly') ? 'yearly' : 'monthly',
          period_end: userData.subscriptionEndDate,
          amount: 0,
          isFreeActivation: true
        });
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading free activation data:', err);
      setError('เกิดข้อผิดพลาดในการโหลดข้อมูล');
      setLoading(false);
    }
  };

  const loadPaymentData = async () => {
    try {
      setLoading(true);

      // Poll for payment confirmation (max 30 seconds)
      let attempts = 0;
      const maxAttempts = 15;

      const checkPayment = async (): Promise<boolean> => {
        if (type === 'subscription') {
          const subscriptionsRef = doc(db, 'omise_subscriptions', chargeId!);
          const subscriptionDoc = await getDoc(subscriptionsRef);

          if (subscriptionDoc.exists()) {
            const data = subscriptionDoc.data();
            if (data.status === 'active') {
              setSubscription(data);
              return true;
            }
          }
        } else {
          const addonsRef = doc(db, 'omise_addons', chargeId!);
          const addonDoc = await getDoc(addonsRef);

          if (addonDoc.exists()) {
            const data = addonDoc.data();
            if (data.status === 'active') {
              setSubscription(data);
              return true;
            }
          }
        }
        return false;
      };

      while (attempts < maxAttempts) {
        const success = await checkPayment();
        if (success) {
          setLoading(false);
          return;
        }

        attempts++;
        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      setError('กำลังประมวลผลการชำระเงิน กรุณารอสักครู่...');
      setLoading(false);
    } catch (err) {
      console.error('Error loading payment data:', err);
      setError('เกิดข้อผิดพลาดในการโหลดข้อมูล');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-20 h-20 bg-white rounded-full shadow-lg flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">กำลังตรวจสอบการชำระเงิน</h2>
          <p className="text-gray-600">กรุณารอสักครู่...</p>
        </div>
      </div>
    );
  }

  if (error && !subscription) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-10 h-10 text-yellow-600 animate-spin" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">กำลังประมวลผล</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-gray-500 mb-6">
            การชำระเงินของคุณกำลังถูกประมวลผล โปรดตรวจสอบที่หน้า Billing Dashboard ในอีกสักครู่
          </p>
          <button
            onClick={() => navigate('/subscription/billing')}
            className="w-full py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
          >
            ไปที่ Billing Dashboard
          </button>
        </div>
      </div>
    );
  }

  const getPlanName = (planId: string) => {
    const plans: Record<string, string> = {
      pro_monthly: 'Pro (รายเดือน)',
      pro_yearly: 'Pro (รายปี)',
      pro_plus_monthly: 'Pro+ (รายเดือน)',
      pro_plus_yearly: 'Pro+ (รายปี)',
    };
    return plans[planId] || planId;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-2xl w-full">
        {/* Success Animation */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full shadow-2xl flex items-center justify-center mx-auto mb-6 animate-bounce">
            <CheckCircle className="w-14 h-14 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            {type === 'free_activation' ? 'เปิดใช้งานสำเร็จ!' : 'ชำระเงินสำเร็จ!'}
          </h1>
          <p className="text-lg text-gray-600">
            {type === 'free_activation' 
              ? 'การเปิดใช้งานด้วยโค้ดส่วนลด 100% สำเร็จแล้ว' 
              : 'ขอบคุณที่ใช้บริการ EduMi'}
          </p>
        </div>

        {/* Payment Details Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-8 py-6">
            <h2 className="text-2xl font-bold text-white">
              {type === 'free_activation' ? 'รายละเอียดการเปิดใช้งาน' : 'รายละเอียดการชำระเงิน'}
            </h2>
          </div>

          <div className="p-8 space-y-6">
            {(type === 'subscription' || type === 'free_activation') && subscription && (
              <>
                <div className="flex items-start gap-4 pb-6 border-b">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Package className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-1">แพ็กเกจ</p>
                    <p className="text-xl font-bold text-gray-900">
                      {getPlanName(subscription.current_plan)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 pb-6 border-b">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-1">รอบบิล</p>
                    <p className="text-xl font-bold text-gray-900">
                      {subscription.billing_cycle === 'yearly' ? 'รายปี' : 'รายเดือน'}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      หมดอายุ: {new Date(subscription.period_end.seconds * 1000).toLocaleDateString('th-TH', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-1">จำนวนเงิน</p>
                    <p className="text-3xl font-bold text-gray-900">
                      {subscription.isFreeActivation ? (
                        <span className="text-green-600">ฟรี (ส่วนลด 100%)</span>
                      ) : (
                        `฿${subscription.amount.toLocaleString()}`
                      )}
                    </p>
                  </div>
                </div>
              </>
            )}

            {type === 'addon' && subscription && (
              <>
                <div className="flex items-start gap-4 pb-6 border-b">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Package className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-1">Add-on</p>
                    <p className="text-xl font-bold text-gray-900">
                      พื้นที่จัดเก็บ {subscription.storage_gb} GB
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-1">จำนวนเงิน</p>
                    <p className="text-3xl font-bold text-gray-900">
                      ฿{subscription.price.toLocaleString()}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Next Steps */}
        {type === 'free_activation' ? (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 mb-6 border-2 border-green-300">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-green-600" />
              เปิดใช้งานสำเร็จ!
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">การเปิดใช้งานของคุณสำเร็จแล้ว</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">คุณสามารถใช้งานฟีเจอร์ทั้งหมดได้ทันที</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">ขอบคุณที่ใช้บริการ EduMi</span>
              </li>
            </ul>
          </div>
        ) : (
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-6 mb-6 border-2 border-yellow-300">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Loader2 className="w-6 h-6 text-yellow-600 animate-spin" />
              รอการอนุมัติจาก Admin
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">การชำระเงินของคุณสำเร็จแล้ว</span>
              </li>
              <li className="flex items-start gap-3">
                <Loader2 className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5 animate-spin" />
                <span className="text-gray-700"><strong>รอ Admin อนุมัติการสมัคร</strong> - คุณจะได้รับการแจ้งเตือนเมื่อได้รับการอนุมัติ</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">หลังจากได้รับการอนุมัติ คุณจะสามารถใช้งานฟีเจอร์ทั้งหมดได้ทันที</span>
              </li>
            </ul>
          </div>
        )}

        {/* Action Buttons */}
        <div className="grid md:grid-cols-2 gap-4">
          {type === 'free_activation' ? (
            <>
              <button
                onClick={() => navigate('/subscription/renew')}
                className="py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-xl transition-all flex items-center justify-center gap-2"
              >
                ดูรายละเอียด Subscription
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate('/instructor/courses/new')}
                className="py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:shadow-xl transition-all flex items-center justify-center gap-2"
              >
                เริ่มสร้างคอร์สเรียน
                <ArrowRight className="w-5 h-5" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate('/admin/unified-payment-approval')}
                className="py-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-xl font-semibold hover:shadow-xl transition-all flex items-center justify-center gap-2"
              >
                ไปที่ Admin Dashboard (รออนุมัติ)
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate('/')}
                className="py-4 bg-white border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:border-blue-600 hover:text-blue-600 transition-all"
              >
                กลับหน้าหลัก
              </button>
            </>
          )}
        </div>

        {/* Support */}
        <p className="text-center text-sm text-gray-500 mt-8">
          หากมีคำถามหรือต้องการความช่วยเหลือ กรุณาติดต่อ{' '}
          <a href="mailto:support@edumi.com" className="text-blue-600 hover:underline">
            support@edumi.com
          </a>
        </p>
      </div>
    </div>
  );
}
